import { useCallback, useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'scalar_desktop_notifications_enabled';
const SW_PATH = '/notification-sw.js';

function readStoredEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function storeEnabled(value: boolean) {
  try {
    localStorage.setItem(STORAGE_KEY, value ? 'true' : 'false');
  } catch {} // eslint-disable-line no-empty
}

function getInitialPermission(): NotificationPermission {
  if (typeof Notification === 'undefined') {
    return 'denied';
  }
  return Notification.permission;
}

export interface BrowserNotificationsState {
  enabled: boolean;
  permission: NotificationPermission;
  requestAndEnable: () => Promise<void>;
  disable: () => void;
  fire: (title: string, body: string) => void;
}

export function useBrowserNotifications(): BrowserNotificationsState {
  const [permission, setPermission] = useState<NotificationPermission>(getInitialPermission);
  const [enabled, setEnabled] = useState(() => readStoredEnabled() && getInitialPermission() === 'granted');

  // Register SW once so showNotification() works (new Notification() is deprecated in Chrome main thread)
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register(SW_PATH).catch(() => {});
    }
  }, []);

  // Sync enabled → false if permission was revoked externally
  useEffect(() => {
    if (permission !== 'granted' && enabled) {
      setEnabled(false);
      storeEnabled(false);
    }
  }, [permission, enabled]);

  const requestAndEnable = useCallback(async () => {
    if (typeof Notification === 'undefined') {
      return;
    }
    const perm = await Notification.requestPermission();
    setPermission(perm);
    if (perm === 'granted') {
      storeEnabled(true);
      setEnabled(true);
    }
  }, []);

  const disable = useCallback(() => {
    storeEnabled(false);
    setEnabled(false);
  }, []);

  const stateRef = useRef({ enabled, permission });
  useEffect(() => {
    stateRef.current = { enabled, permission };
  }, [enabled, permission]);

  const fire = useCallback((title: string, body: string) => {
    const { enabled: en, permission: perm } = stateRef.current;
    if (!en || perm !== 'granted' || typeof Notification === 'undefined') {
      return;
    }

    // Service worker notifications properly reach macOS Notification Center.
    // new Notification() from the main thread is deprecated in Chrome and bypasses the OS.
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready
        .then(reg => reg.showNotification(title, { body, icon: '/favicon.ico' }))
        .catch(() => {
          try {
            new Notification(title, { body }); // eslint-disable-line no-new
          } catch {} // eslint-disable-line no-empty
        });
    } else {
      try {
        new Notification(title, { body }); // eslint-disable-line no-new
      } catch {} // eslint-disable-line no-empty
    }
  }, []);

  return { enabled, permission, requestAndEnable, disable, fire };
}
